import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth.jsx';
import { ThemeProvider } from './lib/theme.jsx';
import { ToastProvider } from './components/ToastProvider.jsx';
import { AppShell } from './components/AppShell.jsx';
import { NavigationProgress } from './components/NavigationProgress.jsx';
import { Loading } from './components/ui/index.js';

import LoginPage from './pages/auth/LoginPage.jsx';
import RegisterPage from './pages/auth/RegisterPage.jsx';
import AcceptInvitePage from './pages/auth/AcceptInvitePage.jsx';
import OverviewPage from './pages/dashboard/OverviewPage.jsx';
import PaymentsPage from './pages/dashboard/PaymentsPage.jsx';
import PaymentDetailPage from './pages/dashboard/PaymentDetailPage.jsx';
import TransactionsPage from './pages/dashboard/TransactionsPage.jsx';
import PaymentLinksPage from './pages/dashboard/PaymentLinksPage.jsx';
import CustomersPage from './pages/dashboard/CustomersPage.jsx';
import { ActivityPage } from './pages/dashboard/ActivityPage.jsx';
import { SetupPage } from './pages/dashboard/SetupPage.jsx';
import ProvidersPage from './pages/developers/ProvidersPage.jsx';
import ApiKeysPage from './pages/developers/ApiKeysPage.jsx';
import WebhooksPage from './pages/developers/WebhooksPage.jsx';
import PaymentMethodsPage from './pages/settings/PaymentMethodsPage.jsx';
import CustomFieldsPage from './pages/settings/CustomFieldsPage.jsx';
import TeamPage from './pages/settings/TeamPage.jsx';
import PosPage from './pos/PosPage.jsx';
import ProfilePage from './pages/settings/ProfilePage.jsx';
import SecurityPage from './pages/settings/SecurityPage.jsx';
import AdminOverviewPage from './pages/admin/AdminOverviewPage.jsx';
import AdminMerchantsPage from './pages/admin/AdminMerchantsPage.jsx';
import AdminMerchantDetailPage from './pages/admin/AdminMerchantDetailPage.jsx';
import AdminHealthPage from './pages/admin/AdminHealthPage.jsx';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage.jsx';
import AdminPaymentDetailPage from './pages/admin/AdminPaymentDetailPage.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';
import { LandingPage } from './pages/marketing/LandingPage.jsx';
import { TermsPage } from './pages/legal/TermsPage.jsx';
import { PrivacyPage } from './pages/legal/PrivacyPage.jsx';

/** Routing waits for the boot-time refresh, or a reload bounces to /login. */
function RequireAuth({ children, adminOnly = false, shared = false }) {
  const { isAuthenticated, isPlatformAdmin, restoring } = useAuth();
  const location = useLocation();

  if (restoring) {
    return <div className="grid min-h-screen place-items-center"><Loading label="Restoring your session…" /></div>;
  }
  if (!isAuthenticated) {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (adminOnly && !isPlatformAdmin) return <Navigate to="/dashboard" replace />;

  /**
   * A platform admin's user record carries a null merchantId, so a merchant
   * screen would render empty or 404 against `/dashboard/*`. Sending them home
   * beats letting them stare at a broken Overview.
   *
   * `shared` is the exception, and it matters: a platform admin still has a
   * password and still needs to change it. Bouncing them off /settings/security
   * made the sidebar item they were given do nothing at all when clicked.
   */
  if (!adminOnly && !shared && isPlatformAdmin) return <Navigate to="/admin" replace />;

  return <AppShell>{children}</AppShell>;
}

function RedirectIfAuthenticated({ children }) {
  const { isAuthenticated, isPlatformAdmin, restoring } = useAuth();
  if (restoring) return <div className="grid min-h-screen place-items-center"><Loading /></div>;
  if (!isAuthenticated) return children;
  return <Navigate to={isPlatformAdmin ? '/admin' : '/dashboard'} replace />;
}

/**
 * The till is authenticated like everything else, but rendered without the
 * dashboard shell — a sidebar is a third of a tablet, and none of it is
 * reachable with the hand that is not holding a coffee.
 */
function RequireAuthBare({ children }) {
  const { isAuthenticated, isPlatformAdmin, restoring } = useAuth();
  const location = useLocation();

  if (restoring) return <div className="grid min-h-screen place-items-center"><Loading /></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (isPlatformAdmin) return <Navigate to="/admin" replace />;
  return children;
}

/**
 * `/` is the public front page.
 *
 * It used to bounce straight to /dashboard, which meant distrxpay.com had
 * nothing to show anyone who was not already a customer. The landing page
 * renders for everybody and changes its own call to action: "Sign in" for a
 * visitor, "Go to your dashboard" for somebody already signed in — including a
 * platform admin, who is sent to /admin rather than to a merchant screen.
 */

const protect = (element, adminOnly = false) => <RequireAuth adminOnly={adminOnly}>{element}</RequireAuth>;

/** Screens that belong to the person, not to the business. */
const shared = (element) => <RequireAuth shared>{element}</RequireAuth>;

export function DashboardApp() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <NavigationProgress />
        {/* FE-5: a single toast provider at the app root. */}
        <ToastProvider>
          <AuthProvider>
            <Routes>
                          <Route path="/login" element={<RedirectIfAuthenticated><LoginPage /></RedirectIfAuthenticated>} />
                          <Route path="/register" element={<RedirectIfAuthenticated><RegisterPage /></RedirectIfAuthenticated>} />
                          {/* Not wrapped in RedirectIfAuthenticated: somebody
                              already signed in on a shared machine still needs
                              to be able to accept an invitation of their own. */}
                          <Route path="/accept-invite" element={<AcceptInvitePage />} />

                          {/* Public, and deliberately reachable while signed in:
                              somebody reads the terms from inside the product as
                              often as from the front page. */}
                          <Route path="/terms" element={<TermsPage />} />
                          <Route path="/privacy" element={<PrivacyPage />} />

                          <Route path="/" element={<LandingPage />} />
                          <Route path="/pos" element={<RequireAuthBare><PosPage /></RequireAuthBare>} />
                          <Route path="/dashboard" element={protect(<OverviewPage />)} />
                          <Route path="/payments" element={protect(<PaymentsPage />)} />
                          <Route path="/payments/:id" element={protect(<PaymentDetailPage />)} />
                          <Route path="/transactions" element={protect(<TransactionsPage />)} />
                          <Route path="/payment-links" element={protect(<PaymentLinksPage />)} />
                          <Route path="/customers" element={protect(<CustomersPage />)} />
                          <Route path="/activity" element={protect(<ActivityPage />)} />
                          <Route path="/setup" element={protect(<SetupPage />)} />
                          <Route path="/providers" element={protect(<ProvidersPage />)} />
                          <Route path="/payment-methods" element={protect(<PaymentMethodsPage />)} />
                          <Route path="/settings/custom-fields" element={protect(<CustomFieldsPage />)} />
                          <Route path="/developers/api-keys" element={protect(<ApiKeysPage />)} />
                          <Route path="/developers/webhooks" element={protect(<WebhooksPage />)} />
                          <Route path="/settings/profile" element={protect(<ProfilePage />)} />
                          <Route path="/settings/team" element={protect(<TeamPage />)} />
                          <Route path="/settings/security" element={shared(<SecurityPage />)} />

            {/* §15: behind a platform_admin role. */}
                          <Route path="/admin" element={protect(<AdminOverviewPage />, true)} />
                          <Route path="/admin/merchants" element={protect(<AdminMerchantsPage />, true)} />
                          <Route path="/admin/merchants/:id" element={protect(<AdminMerchantDetailPage />, true)} />
                          <Route path="/admin/payments" element={protect(<AdminPaymentsPage />, true)} />
                          <Route path="/admin/payments/:id" element={protect(<AdminPaymentDetailPage />, true)} />
                          <Route path="/admin/health" element={protect(<AdminHealthPage />, true)} />

                          <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default DashboardApp;
